import React, {
    useCallback,
    useEffect,
    useState,
} from 'react';
import { IoMdCloseCircle } from 'react-icons/io';
import {
    Alert,
    Button,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Spinner,
} from 'reactstrap';
import { doGET, doPOST } from '../../util/HttpUtil';
import {
    clearAuthResume,
    clearAuthTokens,
    clearStoredAuthMessage,
    clearVerifyIndividualContext,
    markExplicitLogoutLanding,
    setLegacyUnauthUrl,
} from '../../util/authResume';
import { buildAutomationChildTestId, normalizeAutomationTestIdSegment, resolveAutomationTestId } from '../InputField/testId';
import './styles.css';

const DEFAULT_LOGOUT_REASONS = [
    { label: "Don’t have my card handy.", value: "Don’t have my card handy." },
    { label: "Account is not worth the Application fee.", value: "Account is not worth the Application fee." },
    { label: "I'm in a hurry.", value: "I'm in a hurry." },
];

const BROKER_PROFILE_STORAGE_KEY = "BrokerProfile";

const isBrokerAdminSession = () => {
    try {
        const profile = JSON.parse(localStorage.getItem(BROKER_PROFILE_STORAGE_KEY) || "null");
        return profile?.role === "broker_admin";
    } catch (_) {
        return false;
    }
};

const ModalHeaderCentered = ({ children, onClose, testId }) => (
    <ModalHeader
        className="w-100 border-0 justify-content-center"
        data-testid={testId}
    >
        <div className="">
            <span style={{
                width: '100%',
                textAlign: 'center',
                fontWeight: 600,
                fontSize: '24px',
            }}>
                {children}
            </span>
            <IoMdCloseCircle
                onClick={onClose}
                data-testid={buildAutomationChildTestId(testId, "close")}
                style={{
                    cursor: 'pointer',
                    fontSize: '28px',
                    color: '#939393',
                    position: 'absolute',
                    right: '10px',
                    top: '10px'
                }}
            />
        </div>
    </ModalHeader>
);

/* -------------------------------------------------------------------------- */
const LogoutWithReason = ({
    // logoutReasons = DEFAULT_LOGOUT_REASONS,
    allowMultiple = true,
    buttonLabel = 'Logout',
    buttonClassName = 'form-content-btn',
    form_id,
    onLoggedOut = () => { },
    withoutReason = false,
    testId,
}) => {
    /* ----------------------------- local state ----------------------------- */
    const [isOpen, setIsOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [selected, setSelected] = useState([]);
    const [errors, setErrors] = useState(null);
    const [sysReasons, setSysReasons] = useState(DEFAULT_LOGOUT_REASONS);
    const resolvedTestId = resolveAutomationTestId(testId);

    /* ------------------------ fetch system reasons ------------------------ */
    useEffect(() => {
        if (withoutReason || isBrokerAdminSession()) {
            return undefined;
        }

        let mounted = true;
        (async () => {
            try {
                const res = await doGET('/api/sys-config/logoutReasons/detail');
                const list = res?.data?.logoutReasons;

                if (mounted && Array.isArray(list) && list.length) {
                    setSysReasons(list.map((txt) => ({ label: txt, value: txt })));
                }
            } catch (_) {
                /* ignore errors, keep defaults */
            }
        })();
        return () => {
            mounted = false;
        };
    }, [withoutReason]);

    /* ------------------------- computed reason list ------------------------ */

    /* ------------------- selection helpers (checkbox/radio) ----------------- */
    const toggleReason = (reason) => {
        if (allowMultiple) {
            setSelected((prev) => (prev.includes(reason) ? prev.filter((r) => r !== reason) : [...prev, reason]));
        } else {
            setSelected([reason]);
        }
    };
    const isReasonSelected = (reason) => selected.includes(reason);

    /* -------------------- core submit / logout request --------------------- */
    const submitLogout = useCallback(
        async (reasonsArray) => {
            try {
                setSubmitting(true);
                await doPOST('/auth/logout', {
                    form_id,
                    ...(reasonsArray && { logoutReasons : reasonsArray }),
                });
                setTimeout(() => {
                    clearAuthTokens();
                    clearAuthResume();
                    clearStoredAuthMessage();
                    clearVerifyIndividualContext();
                    setLegacyUnauthUrl("");
                    markExplicitLogoutLanding();
                    window.location = '/';
                    onLoggedOut();
                }, 2000);
            } catch (e) {
                setErrors(e.message || 'Something went wrong');
                setSubmitting(false);
            }
        },
        [form_id, onLoggedOut]
    );

    /* ------------------------------- handlers ------------------------------ */
    const handleSubmitWithReason = () => {
        if (selected.length === 0) {
            setErrors('Please choose at least one reason');
            return;
        }
        submitLogout(selected);
    };

    const handleInstantLogout = () => submitLogout(); // no reasons param

    const handleButtonClick = () => (withoutReason ? handleInstantLogout() : setIsOpen(true));

    /* -------------------------------- render -------------------------------- */
    return (
        <>
            {/* trigger button */}
            <Button
                className={buttonClassName}
                onClick={handleButtonClick}
                disabled={submitting}
                data-testid={resolvedTestId}
            >
                {submitting && withoutReason ? <Spinner size="sm" /> : buttonLabel}
            </Button>

            {/* modal (only when reasons are needed) */}
            {!withoutReason && (
                <Modal
                    centered
                    isOpen={isOpen}
                    toggle={() => setIsOpen(false)}
                    className="fv_logout_reason_modal fv_logout_reason_custom_modal"
                    data-testid={buildAutomationChildTestId(resolvedTestId, "modal")}
                >
                    <ModalHeaderCentered
                        onClose={() => setIsOpen(false)}
                        testId={buildAutomationChildTestId(resolvedTestId, "header")}
                    >
                        {submitting ? 'Logging you out…' : 'We’d love your feedback'}
                    </ModalHeaderCentered>

                    <ModalBody data-testid={buildAutomationChildTestId(resolvedTestId, "body")}>
                        {submitting ? (
                            <div className="fv_logout_reason_loading d-flex flex-column align-items-center py-5 gap-3" data-testid={buildAutomationChildTestId(resolvedTestId, "loading")}>
                                <Spinner />
                            </div>
                        ) : (
                            <>
                                <p className="fv_logout_reason_prompt text-center mb-3" data-testid={buildAutomationChildTestId(resolvedTestId, "prompt")}>Help us understand why you’re leaving:</p>
                                <div className="fv_logout_reason_list" data-testid={buildAutomationChildTestId(resolvedTestId, "reason-list")}>
                                    {sysReasons.map((r) => {
                                        const reasonTestId = buildAutomationChildTestId(resolvedTestId, `reason-${normalizeAutomationTestIdSegment(r.value)}`);
                                        return (
                                            <label
                                                key={r.value}
                                                className="fv_logout_reason_item d-flex align-items-center gap-2 mb-2"
                                                data-testid={reasonTestId}
                                            >
                                                <input
                                                    type={allowMultiple ? 'checkbox' : 'radio'}
                                                    checked={isReasonSelected(r.value)}
                                                    onChange={() => toggleReason(r.value)}
                                                    className="fv_logout_reason_checkbox"
                                                    data-testid={buildAutomationChildTestId(reasonTestId, "input")}
                                                />
                                                <span>{r.label}</span>
                                            </label>
                                        );
                                    })}
                                </div>
                                {errors && (
                                    <Alert color="danger" className="fv_logout_reason_error" data-testid={buildAutomationChildTestId(resolvedTestId, "error")}>
                                        {errors}
                                    </Alert>
                                )}
                            </>
                        )}
                    </ModalBody>

                    {!submitting && (
                        <ModalFooter className="justify-content-between" data-testid={buildAutomationChildTestId(resolvedTestId, "footer")}>
                            <Button className="form-content-btn-outline" onClick={() => setIsOpen(false)} data-testid={buildAutomationChildTestId(resolvedTestId, "cancel")}>
                                Cancel
                            </Button>
                            <Button className="form-content-btn" onClick={handleSubmitWithReason} data-testid={buildAutomationChildTestId(resolvedTestId, "confirm")}>
                                {submitting ? <Spinner size="sm" /> : 'Logout'}
                            </Button>
                        </ModalFooter>
                    )}
                </Modal>
            )}
        </>
    );
};

export default LogoutWithReason;
